import React, { useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useAIAnswerContext } from '../../../../ai-answer-context';
import { InlineCitationGroup, type InlineCitationGroupProps } from './inline-citation-group';
import { InlineSmartLinkCitation } from './inline-smart-link-citation';

export type InlineCitationProps = {
	localId: string;
	text: string;
	color?: string;
};

export const InlineCitation = ({ localId }: InlineCitationProps): React.JSX.Element | null => {
	const { commonAttributes } = useAIAnswerContext();
	const { sources } = commonAttributes;
	const useSmartLinks = fg('kd_sain_reduce_inline_citations');

	const inlineCitationData: (InlineCitationGroupProps & { sourceIndex?: number }) | undefined =
		useMemo(() => {
			if (!sources || sources.length === 0) {
				return undefined;
			}

			try {
				const parsedCitationIndices = JSON.parse(localId) as number[];
				if (useSmartLinks) {
					const sourceIndex = parsedCitationIndices.find((idx) => !!sources[idx - 1]?.url);
					return sourceIndex === undefined
						? undefined
						: { parsedSources: [sources[sourceIndex - 1]], sourceIndex };
				}
				const parsedSources = parsedCitationIndices.map((idx) => sources[idx - 1]);
				const citationUrls = parsedSources.map((source) => source.url);
				return {
					citationUrls,
					parsedSources,
				};
			} catch {
				return undefined;
			}
		}, [sources, localId, useSmartLinks]);

	if (useSmartLinks) {
		return inlineCitationData?.sourceIndex !== undefined ? (
			<InlineSmartLinkCitation
				source={inlineCitationData.parsedSources[0]}
				sourceIndex={inlineCitationData.sourceIndex}
			/>
		) : null;
	}

	return !!inlineCitationData ? (
		<InlineCitationGroup parsedSources={inlineCitationData.parsedSources} />
	) : null;
};
